import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SeoJsonLd from '../../components/SeoJsonLd';

// 오늘 날짜의 시작 숫자를 가져오는 함수
const getTodayStartNumber = () => {
  const today = new Date();
  const dateString = `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;
  
  let hash = 0;
  for (let i = 0; i < dateString.length; i++) {
    const char = dateString.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  
  const randomStart = 50 + (Math.abs(hash) % 51);
  return randomStart;
};

// 어제까지의 누적 사용자 수를 계산하는 함수
const getAccumulatedUsers = () => {
  const today = new Date();
  const startDate = new Date('2025-01-01'); // 서비스 시작일
  const daysSinceStart = Math.floor((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  
  // 하루 평균 50~100명 증가
  const avgDailyUsers = 75;
  return daysSinceStart * avgDailyUsers;
};

export default function Home() {
  const navigate = useNavigate();
  const [businessAddress, setBusinessAddress] = useState('');
  const [businessArea, setBusinessArea] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [hasLessThan5Employees, setHasLessThan5Employees] = useState('');
  const [isExcludedIndustry, setIsExcludedIndustry] = useState('');
  const [hasDemolitionStarted, setHasDemolitionStarted] = useState('');
  const [isCalculating, setIsCalculating] = useState(false);
  const [showEmployeeDetails, setShowEmployeeDetails] = useState(false);

  // 실시간 이용현황 상태
  const [todayUsers, setTodayUsers] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);

  // 실시간 이용현황 초기화 및 카운트
  useEffect(() => {
    // 오늘의 시작 숫자
    const startNumber = getTodayStartNumber();
    
    // 현재 시간 기준으로 경과 시간 계산 (분 단위)
    const now = new Date();
    const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
    const minutesSinceMidnight = Math.floor((now.getTime() - midnight.getTime()) / (1000 * 60));
    
    // 하루 1440분 기준, 시작 숫자에서 30~80명 증가하도록 계산
    const maxIncrease = 30 + (Math.abs(getTodayStartNumber()) % 51); // 30~80 사이
    const currentIncrease = Math.floor((minutesSinceMidnight / 1440) * maxIncrease);
    const currentTodayUsers = startNumber + currentIncrease;
    
    setTodayUsers(currentTodayUsers);

    // 누적 사용자 수: 35,062명 + 어제까지의 누적 + 오늘 이용자 수
    const baseTotal = 35062;
    const accumulatedUsers = getAccumulatedUsers();
    setTotalUsers(baseTotal + accumulatedUsers + currentTodayUsers);

    // 1분마다 카운트 증가
    const interval = setInterval(() => {
      setTodayUsers(prev => {
        const newCount = prev + 1;
        setTotalUsers(baseTotal + accumulatedUsers + newCount);
        return newCount;
      });
    }, 60000); // 1분마다 1씩 증가

    return () => clearInterval(interval);
  }, []);

  const handleAnswerChange = (questionId: number, answer: string) => {
    if (questionId === 4) {
      setHasLessThan5Employees(answer);
    } else if (questionId === 5) {
      setIsExcludedIndustry(answer);
    }
  };

  const calculateSupport = () => {
    // 필수 입력 검증
    if (!businessAddress) {
      alert('사업장 주소를 입력해주세요.');
      return;
    }

    if (!businessArea) {
      alert('사업장 평수를 입력해주세요.');
      return;
    }

    if (!hasDemolitionStarted) {
      alert('철거/원상복구 진행 여부를 선택해주세요.');
      return;
    }

    if (!hasLessThan5Employees) {
      alert('상시근로자 5인미만 여부를 선택해주세요.');
      return;
    }

    if (!isExcludedIndustry) {
      alert('지원금 제외업종 해당 여부를 선택해주세요.');
      return;
    }

    // 로딩 시작
    setIsCalculating(true);

    // 2-3초 후 결과 페이지로 이동
    setTimeout(() => {
      // 상시근로자 5인미만이 아닌 경우
      if (hasLessThan5Employees === '아니오') {
        navigate('/result', { 
          state: { 
            amount: 0,
            reason: '상시근로자 5인 이상',
            businessAddress,
            businessArea,
            hasDemolitionStarted
          } 
        });
        return;
      }

      // 지원금 제외업종에 해당하는 경우
      if (isExcludedIndustry === '예') {
        navigate('/result', { 
          state: { 
            amount: 0,
            reason: '지원금 제외업종에 해당',
            businessAddress,
            businessArea,
            hasDemolitionStarted
          } 
        });
        return;
      }

      // 평수에 따른 지원금 계산
      let pyeong = parseFloat(businessArea);
      
      // 30평 이상인 경우 30평으로 제한
      if (pyeong > 30) {
        pyeong = 30;
      }
      
      const totalAmount = pyeong * 200000; // 1평당 20만원
      
      navigate('/result', { 
        state: { 
          amount: totalAmount,
          businessAddress,
          businessArea: pyeong >= 30 ? '30평이상' : `${pyeong}평`,
          businessType,
          hasDemolitionStarted
        } 
      });
    }, 2500);
  };

  const resetForm = () => {
    setBusinessArea('');
    setBusinessAddress('');
    setHasLessThan5Employees('');
    setIsExcludedIndustry('');
    setBusinessType('');
    setHasDemolitionStarted('');
  };

  return (
    <>
      <SeoJsonLd type="WebSite" />
      <SeoJsonLd 
        type="WebPage" 
        data={{
          name: '폐업지원금 간편 계산기',
          description: '7가지 정보를 입력하면 폐업지원금을 즉시 확인가능! 정확한 폐업지원금 계산 서비스를 제공합니다.'
        }}
      />
      <div className="min-h-screen bg-white overflow-x-hidden">
        {/* 로딩 오버레이 */}
        {isCalculating && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 max-w-md mx-4 text-center">
              <div className="w-20 h-20 mx-auto mb-6 relative">
                <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-full animate-ping opacity-75"></div>
                <div className="relative w-20 h-20 flex items-center justify-center bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full">
                  <i className="ri-calculator-line text-white text-4xl animate-pulse"></i>
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3 md:mb-4">
                지원금 계산 중입니다...
              </h3>
              <p className="text-sm text-gray-600">
                잠시만 기다려주세요
              </p>
              <div className="mt-6 flex justify-center gap-2">
                <div className="w-3 h-3 bg-teal-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-3 h-3 bg-teal-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-3 h-3 bg-teal-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <header className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {/* 로고: 계산기 아이콘 + 텍스트 */}
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-full">
                    <i className="ri-calculator-line text-white text-2xl"></i>
                  </div>
                  <span className="text-gray-900 font-bold text-lg">폐업지원금 간편 계산기</span>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="bg-gradient-to-br from-teal-50 to-cyan-50 py-8 md:py-12 lg:py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4 leading-tight">
              폐업지원금을<br className="md:hidden" /> 간편하게 계산하세요
            </h2>
            <p className="text-base md:text-lg text-gray-600 mb-6 md:mb-8 leading-relaxed">
              7가지 정보만 입력하면 1분이내 확인가능
            </p>
            <div className="flex flex-row items-center justify-center gap-4 md:gap-6 text-xs md:text-sm text-gray-500 mb-8 md:mb-10">
              <div className="flex items-center gap-2">
                <i className="ri-check-line text-teal-600 text-xl"></i>
                <span>간편한 계산</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-shield-check-line text-teal-600 text-xl"></i>
                <span>정확한 결과</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-time-line text-teal-600 text-xl"></i>
                <span>즉시 확인</span>
              </div>
            </div>

            {/* 실시간 이용현황 */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 md:p-8 max-w-2xl mx-auto">
              <div className="flex items-center justify-center gap-2 mb-4 md:mb-6">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900">실시간 이용현황</h3>
              </div>
              <div className="grid grid-cols-2 gap-4 md:gap-6">
                <div className="text-center">
                  <p className="text-xs md:text-sm text-gray-600 mb-2">오늘 이용자</p>
                  <div className="flex items-center justify-center gap-1">
                    <p className="text-2xl md:text-3xl font-bold text-teal-600">{todayUsers.toLocaleString()}</p>
                    <p className="text-xs text-gray-500">명</p>
                  </div>
                </div>
                <div className="text-center">
                  <p className="text-xs md:text-sm text-gray-600 mb-2">누적 이용자</p>
                  <div className="flex items-center justify-center gap-1">
                    <p className="text-2xl md:text-3xl font-bold text-gray-900">{totalUsers.toLocaleString()}</p>
                    <p className="text-xs text-gray-500">명</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Form */}
        <section className="py-8 md:py-12 lg:py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 md:p-8">
              <div className="space-y-8 md:space-y-10">
                {/* 사업장 주소 */}
                <div className="pb-6 md:pb-8 border-b border-gray-200">
                  <div className="text-center">
                    <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-3 md:mb-4">
                      <i className="ri-map-pin-line text-teal-600 text-base md:text-lg"></i>
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">사업장 주소</h3>
                    <p className="text-xs md:text-sm text-gray-600 mb-3 md:mb-4 leading-relaxed">
                      사업장의 전체 주소를<br className="md:hidden" /> 입력해주세요
                    </p>
                    <input
                      type="text"
                      value={businessAddress}
                      onChange={(e) => setBusinessAddress(e.target.value)}
                      placeholder="예: 서울특별시 강남구 테헤란로 123"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-text"
                    />
                  </div>
                </div>

                {/* 사업장 평수 */}
                <div className="pb-6 md:pb-8 border-b border-gray-200">
                  <div className="text-center">
                    <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-3 md:mb-4">
                      <i className="ri-home-line text-teal-600 text-base md:text-lg"></i>
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
                      사업장 평수를 입력하세요
                    </h3>
                    <p className="text-xs md:text-sm text-gray-600 mb-3 md:mb-4 leading-relaxed">
                      소수점일 경우 올림해서 입력<br className="md:hidden" /> (예: 23.3평 ▶ 24평)
                    </p>
                    <input
                      type="number"
                      value={businessArea}
                      onChange={(e) => setBusinessArea(e.target.value)}
                      placeholder="예: 24"
                      min="1"
                      step="1"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-text"
                    />
                  </div>
                </div>

                {/* 철거/원상복구 진행 여부 */}
                <div className="pb-6 md:pb-8 border-b border-gray-200">
                  <div className="text-center">
                    <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-3 md:mb-4">
                      <i className="ri-hammer-line text-teal-600 text-base md:text-lg"></i>
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 leading-tight">
                      철거, 원상복구를 하셨나요?
                    </h3>
                    <p className="text-xs md:text-sm text-gray-600 mb-3 md:mb-4">
                      현재 철거 또는 원상복구 진행 여부를 선택해주세요.
                    </p>
                    <div className="flex gap-3 md:gap-4 justify-center max-w-md mx-auto">
                      <button
                        type="button"
                        onClick={() => setHasDemolitionStarted('예')}
                        className={`flex-1 px-4 md:px-6 py-3 rounded-lg text-sm md:text-base font-medium transition-all whitespace-nowrap cursor-pointer ${
                          hasDemolitionStarted === '예'
                            ? 'bg-teal-600 text-white shadow-md'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        예
                      </button>
                      <button
                        type="button"
                        onClick={() => setHasDemolitionStarted('아니오')}
                        className={`flex-1 px-4 md:px-6 py-3 rounded-lg text-sm md:text-base font-medium transition-all whitespace-nowrap cursor-pointer ${
                          hasDemolitionStarted === '아니오'
                            ? 'bg-teal-600 text-white shadow-md'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        아니오
                      </button>
                    </div>
                  </div>
                </div>

                {/* 상시근로자 안내 */}
                <div className="pb-6 md:pb-8 border-b border-gray-200">
                  <div className="text-center">
                    <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-3 md:mb-4">
                      <i className="ri-team-line text-teal-600 text-base md:text-lg"></i>
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 leading-tight">
                      상시근로자 5인미만 인가요?
                    </h3>
                    <p className="text-xs md:text-sm text-gray-600 mb-3 md:mb-4">
                      상시근로자는 "대표 제외 + 급여 지급 근로자" 기준입니다.
                    </p>
                    <div className="flex gap-3 md:gap-4 justify-center max-w-md mx-auto">
                      <button
                        type="button"
                        onClick={() => setHasLessThan5Employees('예')}
                        className={`flex-1 px-4 md:px-6 py-3 rounded-lg text-sm md:text-base font-medium transition-all whitespace-nowrap cursor-pointer ${
                          hasLessThan5Employees === '예'
                            ? 'bg-teal-600 text-white shadow-md'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        예
                      </button>
                      <button
                        type="button"
                        onClick={() => setHasLessThan5Employees('아니오')}
                        className={`flex-1 px-4 md:px-6 py-3 rounded-lg text-sm md:text-base font-medium transition-all whitespace-nowrap cursor-pointer ${
                          hasLessThan5Employees === '아니오'
                            ? 'bg-teal-600 text-white shadow-md'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        아니오
                      </button>
                    </div>
                    
                    {/* 상시근로자 기준 자세히 보기 토글 */}
                    <button
                      type="button"
                      onClick={() => setShowEmployeeDetails(!showEmployeeDetails)}
                      className="mt-3 md:mt-4 text-xs md:text-sm text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1 mx-auto cursor-pointer"
                    >
                      <span>상시근로자 기준 자세히 보기</span>
                      <i className={`ri-arrow-${showEmployeeDetails ? 'up' : 'down'}-s-line text-base`}></i>
                    </button>

                    {/* 상세 설명 (토글) */}
                    {showEmployeeDetails && (
                      <div className="mt-3 md:mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4 md:p-5 max-w-2xl mx-auto">
                        <ul className="space-y-2 text-xs md:text-sm text-gray-700 leading-relaxed text-left">
                          <li className="flex items-start gap-2">
                            <span className="text-blue-600 mt-0.5">•</span>
                            <span>제조업, 건설업, 운수업인 경우는<br className="md:hidden" /> 10인 미만</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-blue-600 mt-0.5">•</span>
                            <span>상시근로자는 사업주 본인을 제외한<br className="md:hidden" /> 사업장에서 근무하며</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-blue-600 mt-0.5">•</span>
                            <span>근로계약서를 작성한 모든 근로자를<br className="md:hidden" /> 뜻합니다</span>
                          </li>
                        </ul>
                      </div>
                    )}

                    {hasLessThan5Employees === '아니오' && (
                      <div className="mt-3 md:mt-4 flex items-start gap-2 text-red-600 justify-center max-w-md mx-auto">
                        <i className="ri-error-warning-line text-base md:text-lg mt-0.5"></i>
                        <p className="text-xs md:text-sm font-medium leading-relaxed">
                          상시근로자가 5인 이상으로<br className="md:hidden" /> 폐업지원금 신청이 어렵습니다.<br className="md:hidden" /> 다시 확인해주세요
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* 지원금 제외업종에 해당 되시나요? */}
                <div className="pb-6 md:pb-8 border-b border-gray-200">
                  <div className="text-center">
                    <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-3 md:mb-4">
                      <i className="ri-store-line text-teal-600 text-base md:text-lg"></i>
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 leading-tight">
                      지원금 제외업종에<br className="md:hidden" /> 해당 되시나요?
                    </h3>
                    <p className="text-xs md:text-sm text-gray-600 mb-3 md:mb-4 leading-relaxed">
                      다음 업종은 지원금 수령이 제한됩니다
                    </p>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 md:p-5 mb-3 md:mb-4 max-w-2xl mx-auto">
                      <ul className="space-y-2 text-xs md:text-sm text-gray-700 leading-relaxed text-left">
                        <li className="flex items-start gap-2">
                          <span className="text-blue-600 mt-0.5">•</span>
                          <span>전문직 직종(병원,변호,세무,법무 등)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-blue-600 mt-0.5">•</span>
                          <span>도박/불법 유흥업종/성인용품점 등</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-blue-600 mt-0.5">•</span>
                          <span>비영리사업자 및 비영리법인 (고유번호증소지자)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-blue-600 mt-0.5">•</span>
                          <span>그 외 소상공인 자격에 해당되는<br />모든 자영업은 폐업지원금 수령 가능</span>
                        </li>
                      </ul>
                    </div>
                    <div className="flex gap-3 md:gap-4 justify-center max-w-md mx-auto">
                      <button
                        type="button"
                        onClick={() => handleAnswerChange(5, '예')}
                        className={`flex-1 px-4 md:px-6 py-3 rounded-lg text-sm md:text-base font-medium transition-all whitespace-nowrap cursor-pointer ${
                          isExcludedIndustry === '예'
                            ? 'bg-teal-600 text-white shadow-md'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        예
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAnswerChange(5, '아니오')}
                        className={`flex-1 px-4 md:px-6 py-3 rounded-lg text-sm md:text-base font-medium transition-all whitespace-nowrap cursor-pointer ${
                          isExcludedIndustry === '아니오'
                            ? 'bg-teal-600 text-white shadow-md'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        아니오
                      </button>
                    </div>
                    {isExcludedIndustry === '예' && (
                      <div className="mt-3 md:mt-4 flex items-start gap-2 text-red-600 justify-center max-w-md mx-auto">
                        <i className="ri-error-warning-line text-base md:text-lg mt-0.5"></i>
                        <p className="text-xs md:text-sm font-medium leading-relaxed">
                          지원금 제외 업종으로<br className="md:hidden" /> 폐업지원금 신청이 어렵습니다.<br className="md:hidden" /> 다시 확인해주세요
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* 업종 입력 */}
                <div className="pb-6 md:pb-8 border-b border-gray-200">
                  <div className="text-center">
                    <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-3 md:mb-4">
                      <i className="ri-briefcase-line text-teal-600 text-base md:text-lg"></i>
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
                      업종을 입력해주세요
                    </h3>
                    <input
                      type="text"
                      value={businessType}
                      onChange={(e) => setBusinessType(e.target.value)}
                      placeholder="예: 식당"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-text"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-8 md:mt-10 flex flex-col sm:flex-row gap-3 md:gap-4">
                <button
                  type="button"
                  onClick={calculateSupport}
                  className="flex-1 bg-teal-600 text-white px-6 md:px-8 py-3 md:py-4 rounded-lg text-base md:text-lg font-semibold hover:bg-teal-700 transition-colors shadow-md whitespace-nowrap cursor-pointer"
                >
                  지원금 계산하기
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 md:px-8 py-3 md:py-4 border-2 border-gray-300 text-gray-700 rounded-lg text-base md:text-lg font-semibold hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  초기화
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Info Section */}
        <section className="py-8 bg-gray-50">
        </section>

        {/* Footer */}
        <footer className="bg-teal-600 text-white py-8 md:py-10 lg:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-3 gap-6 md:gap-8 mb-6 md:mb-8">
              <div>
                <h4 className="text-base md:text-lg font-bold mb-3 md:mb-4">폐업지원금 간편 계산기</h4>
                <p className="text-xs md:text-sm text-teal-100 leading-relaxed">
                  간편하고 정확한 폐업지원금<br className="md:hidden" /> 계산 서비스를 제공합니다.
                </p>
              </div>
              
              <div>
                <h4 className="text-base md:text-lg font-bold mb-3 md:mb-4">문의하기</h4>
                <div className="space-y-2 text-xs md:text-sm text-teal-100">
                  <p className="flex items-center gap-2">
                    <i className="ri-phone-line"></i>
                    <span>1555-6989</span>
                  </p>
                  <p className="flex items-center gap-2">
                    <i className="ri-mail-line"></i>
                    <span>verum614@naver.com</span>
                  </p>
                </div>
              </div>
              
              <div>
                <h4 className="text-base md:text-lg font-bold mb-3 md:mb-4">회사 정보</h4>
                <div className="space-y-2 text-xs md:text-sm text-teal-100">
                  <p>주식회사 베룸</p>
                  <p>사업자번호: 260-86-03020</p>
                  <p>대표자: 현진호</p>
                </div>
              </div>
            </div>
            
            <div className="border-t border-teal-500 pt-6 md:pt-8 flex flex-col md:flex-row justify-between items-center gap-3 md:gap-4">
              <p className="text-xs md:text-sm text-teal-100">
                © 2025 폐업지원금 계산기. All rights reserved.
              </p>
              <a 
                href="https://readdy.ai/?origin=logo" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs md:text-sm text-teal-100 hover:text-white transition-colors cursor-pointer"
              >
                Website Builder
              </a>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
